import { useState, Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/components/theme-provider';
import { Bell, Moon, Sun, ExternalLink, LogOut, KeyRound, Settings } from 'lucide-react';
import Sidebar from './sidebar';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ChangePasswordDialog } from '@/components/change-password-dialog';
import { cn } from '@/lib/utils';

// Lazy load all dashboard pages
const Overview = lazy(() => import('./overview'));
const ClientsPage = lazy(() => import('./clients'));
const SuppliersPage = lazy(() => import('./suppliers'));
const SuppliesPage = lazy(() => import('./supplies'));
const InventoryBalancePage = lazy(() => import('./supplies/inventory-balance'));
const ProductsPage = lazy(() => import('./products'));
const ProjectsPage = lazy(() => import('./projects'));
const MetallurgiesPage = lazy(() => import('./metallurgies'));
const SettingsPage = lazy(() => import('./settings'));
const OrdersPage = lazy(() => import('./orders'));
const FinancialPage = lazy(() => import('./financial'));
const TasksPage = lazy(() => import('./tasks'));
const ProductionIndicatorsPage = lazy(() => import('./production-indicators'));
const ShippingPage = lazy(() => import('./shipping'));
const BlacksmithsPage = lazy(() => import('./blacksmiths'));
const BreakevenPage = lazy(() => import('./breakeven'));

const externalLinks = [
  {
    name: '🛒 Dashboard Compras',
    url: 'https://gregarious-concha-3f06c1.netlify.app/'
  },
  {
    name: '📊 Dashboard OKR',
    url: 'https://wonderful-monstera-87cb16.netlify.app/'
  },
  {
    name: '🚚 Dashboard de Logística',
    url: 'https://illustrious-melomakarona-fec749.netlify.app/'
  },
  {
    name: '🏭 Dashboard SINPLE',
    url: 'http://26.208.61.19:3000/'
  },
  {
    name: '🔧 Abertura de Chamado TI',
    url: 'https://grupoplantao.srv.br/ticket'
  },
  {
    name: '📦 Catálogo Almoxarifado',
    url: 'https://www.meucomercio.com.br/plantao'
  },
  {
    name: '🏬 SGAI - Sistema de Gestão de Almoxarifado Inteligente',
    url: 'https://rococo-valkyrie-fc90ac.netlify.app/'
  },
  {
    name: '🖼️ Adicionar imagens URL',
    url: 'https://imgur.com/'
  },
  {
    name: '☁️ OwnCloud',
    url: 'https://grupoplantao.srv.br/cloud/'
  },
  {
    name: '👤 Minha Plantão',
    url: 'http://grupoplantao2.ddns.net:8090/minha_plantao/'
  },
  {
    name: '📦 ReStock Colab – Sistema de Itens Obsoletos',
    url: 'https://jovial-eclair-58dfa0.netlify.app/'
  }
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const { user, loading: authLoading, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  const getPageInfo = (pathname: string) => {
    if (pathname.includes('/orders')) return { title: 'Ordens de Produção', section: 'Operacional' };
    if (pathname.includes('/production-indicators')) return { title: 'Indicadores de Produção', section: 'Gestão' };
    if (pathname.includes('/breakeven')) return { title: 'Ponto de Equilíbrio', section: 'Custos & Finanças' };
    if (pathname.includes('/inventory-balance')) return { title: 'Balanço de Estoque', section: 'Estoque & Insumos' };
    if (pathname.includes('/supplies')) return { title: 'Insumos Industriais', section: 'Estoque & Insumos' };
    if (pathname.includes('/products')) return { title: 'Produtos Acabados', section: 'Estoque & Insumos' };
    if (pathname.includes('/shipping')) return { title: 'Romaneios de Expedição', section: 'Logística' };
    if (pathname.includes('/clients')) return { title: 'Gestão de Clientes', section: 'Cadastros' };
    if (pathname.includes('/blacksmiths')) return { title: 'Serralheiros', section: 'Cadastros' };
    if (pathname.includes('/projects')) return { title: 'Projetos de Obra', section: 'Cadastros' };
    if (pathname.includes('/metallurgies')) return { title: 'Metalúrgicas Parceiras', section: 'Cadastros' };
    if (pathname.includes('/suppliers')) return { title: 'Fornecedores', section: 'Cadastros' };
    if (pathname.includes('/financial')) return { title: 'Gestão Financeira', section: 'Gestão' };
    if (pathname.includes('/tasks')) return { title: 'Quadro de Tarefas', section: 'Operacional' };
    if (pathname.includes('/settings')) return { title: 'Configurações & Usuários', section: 'Sistema' };
    return { title: 'Visão Geral Operacional', section: 'Início' };
  };

  const pageInfo = getPageInfo(location.pathname);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth/login');
    }
  }, [user, authLoading, navigate]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      <Sidebar 
        open={sidebarOpen} 
        onToggle={() => setSidebarOpen(!sidebarOpen)} 
      />
      
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300 min-w-0 w-full overflow-x-hidden",
        sidebarOpen ? 'ml-64' : 'ml-16'
      )}>
        {/* Header Superior Moderno */}
        <header className="h-16 border-b bg-card/90 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between gap-4 sticky top-0 z-30 shadow-xs">
          {/* Breadcrumb e Título da Página Atual */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex flex-col min-w-0">
              <span className="text-[11px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                {pageInfo.section}
              </span>
              <h1 className="text-base sm:text-lg font-bold text-foreground truncate leading-tight">
                {pageInfo.title}
              </h1>
            </div>
          </div>

          {/* Ações Rápidas & Perfil */}
          <div className="flex items-center gap-2 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
              >
                <ExternalLink className="h-5 w-5" />
                <span className="sr-only">Links Externos</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {externalLinks.map((link) => (
                <DropdownMenuItem
                  key={link.url}
                  onClick={() => window.open(link.url, '_blank')}
                  className="cursor-pointer"
                >
                  {link.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            className="relative"
          >
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
            <span className="sr-only">Notificações</span>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          >
            {theme === 'light' ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
            <span className="sr-only">Alternar tema</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 pl-2 pr-2.5 py-1 h-9 rounded-full hover:bg-muted"
              >
                <div className="h-7 w-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-semibold">
                  {user?.email ? user.email.slice(0, 2).toUpperCase() : 'US'}
                </div>
                <div className="hidden md:flex flex-col items-start text-left text-xs leading-none">
                  <span className="font-medium truncate max-w-[150px]">{user?.email}</span>
                  {user?.email?.toLowerCase() === 'reiverson.mascarenhas@grupoplantao.srv.br' ? (
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold mt-0.5">Master</span>
                  ) : (
                    <span className="text-[10px] text-muted-foreground mt-0.5">Operador</span>
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs text-muted-foreground border-b mb-1">
                Conectado como:
                <p className="font-semibold text-foreground truncate mt-0.5">{user?.email}</p>
              </div>
              <DropdownMenuItem
                onClick={() => setPasswordDialogOpen(true)}
                className="cursor-pointer gap-2 text-xs"
              >
                <KeyRound className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                Alterar Minha Senha
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate('/dashboard/settings')}
                className="cursor-pointer gap-2 text-xs"
              >
                <Settings className="h-4 w-4" />
                Gestão de Usuários
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer gap-2 text-xs text-red-600 dark:text-red-400"
              >
                <LogOut className="h-4 w-4" />
                Sair do Sistema
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

            <ChangePasswordDialog
              open={passwordDialogOpen}
              onOpenChange={setPasswordDialogOpen}
            />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-muted/20 p-4 sm:p-6 lg:p-8">
          <div className="max-w-[1600px] w-full mx-auto">
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner />
              </div>
            }>
              <Routes>
                <Route index element={<Overview />} />
                <Route path="clients/*" element={<ClientsPage />} />
                <Route path="suppliers/*" element={<SuppliersPage />} />
                <Route path="supplies/*" element={<SuppliesPage />} />
                <Route path="supplies/inventory-balance/*" element={<InventoryBalancePage />} />
                <Route path="products/*" element={<ProductsPage />} />
                <Route path="projects/*" element={<ProjectsPage />} />
                <Route path="metallurgies/*" element={<MetallurgiesPage />} />
                <Route path="settings/*" element={<SettingsPage />} />
                <Route path="orders/*" element={<OrdersPage />} />
                <Route path="financial/*" element={<FinancialPage />} />
                <Route path="tasks/*" element={<TasksPage />} />
                <Route path="production-indicators/*" element={<ProductionIndicatorsPage />} />
                <Route path="breakeven/*" element={<BreakevenPage />} />
                <Route path="shipping/*" element={<ShippingPage />} />
                <Route path="blacksmiths/*" element={<BlacksmithsPage />} />
              </Routes>
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}